<template>
  <div v-if="showBanner" class="email-verification-banner">
    <div class="banner-content">
      <div class="banner-text">
        <div class="title-row">
          <v-icon color="#856404" size="small" class="banner-icon">mdi-alert-circle</v-icon>
          <span class="banner-title">{{ $t('dashboard.user.verify-email.banner.title') }}</span>
        </div>
        <span class="banner-subtitle">{{ $t('dashboard.user.verify-email.banner.subtitle') }}</span>
      </div>
      
      <div class="banner-actions">
        <!-- See VerifyEmailView: pressable whatever the limits say, with the state beside it. -->
        <button
          :disabled="resendLoading"
          @click="resendVerification"
          class="resend-button"
          :class="{ 'disabled': resendLoading }"
        >
          <v-icon size="small" class="resend-icon">mdi-email-send</v-icon>
          <span class="resend-text">{{ $t('dashboard.user.verify-email.banner.resend-verification') }}</span>
        </button>
        <span v-if="showResendCountdown" class="resend-hint">
          {{ $t('dashboard.user.verify-email.banner.resend-cooldown', { seconds: resendCooldownSeconds }) }}
        </span>
        <span v-else-if="isResendDailyLimitReached" class="resend-hint">
          {{ $t('dashboard.user.verify-email.banner.resend-limit') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import AuthRepository from '@/js/api/AuthRepository'
import { VERIFICATION_RESEND_LIMIT_CODES } from '@/js/api/AuthRepository'
import errorUtil from '@/js/utils/errorUtil'
import { mapState } from 'vuex'
import emailResendCooldown from '@/domains/account/util/emailResendCooldown'

export default {
  name: 'EmailVerificationBanner',
  // Same button as VerifyEmailView, same limits: one module decides when it is usable.
  mixins: [emailResendCooldown],
  data() {
    return {
      resendLoading: false,
      resendCount: 0
    }
  },
  computed: {
    ...mapState('user', ['user']),
    showBanner() {
      return this.user && this.user.emailVerified === false
    } 
  },
  methods: {
    async resendVerification() {
      if (!this.user || !this.user.email) {
        this.$toast.error('User email not found')
        return
      }
      
      // Only the in-flight guard: a limit is the API's answer to give, not ours to predict.
      if (this.resendLoading) return
      
      this.resendLoading = true
      this.resendCount++
      
      try {
        const result = await AuthRepository.resendVerificationEmail(this.user.email)
        
        // Different messages based on attempt count
        if (this.resendCount === 1) {
          this.$toast.success(this.$t('dashboard.user.verify-email.banner.resend-verification-success'))
        } else if (this.resendCount >= 2) {
          this.$toast.success(this.$t('dashboard.user.verify-email.banner.resend-verification-success-final'))
        }
        this.applyResendResult(result)
      } catch (error) {
        console.error('Failed to resend verification:', error)
        let errorMessage = errorUtil.getSingleErrorMessage(error)
        this.$toast.error(errorMessage || this.$t('dashboard.user.verify-email.banner.resend-verification-error'))
        this.applyResendRefusal(error, VERIFICATION_RESEND_LIMIT_CODES)
      } finally {
        this.resendLoading = false
      }
    }
  }
}
</script>

<style scoped>
.email-verification-banner {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border: 1px solid #ffc107;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);
  animation: slideInDown 0.3s ease-out;
}

.banner-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  min-height: 48px;
}

.banner-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.banner-icon {
  flex-shrink: 0;
}

.banner-title {
  font-weight: 600;
  color: #856404;
  font-size: 15px;
  line-height: 1.3;
}

.banner-subtitle {
  color: #856404;
  font-size: 13px;
  opacity: 0.9;
}

.banner-actions {
  flex-shrink: 0;
}

.resend-hint {
  color: #856404;
  font-size: 12px;
  align-self: center;
}
.resend-button {
  background: #856404;
  border: 1px solid #856404;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-height: 36px;
}

.resend-button:hover:not(.disabled) {
  background: #6d5203;
  border-color: #6d5203;
  box-shadow: 0 2px 6px rgba(133, 100, 4, 0.4);
  transform: translateY(-1px);
}

.resend-button:active:not(.disabled) {
  background: #5a4402;
  border-color: #5a4402;
  transform: translateY(0);
}

.resend-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #a0a0a0;
  border-color: #a0a0a0;
  box-shadow: none;
}

.resend-icon {
  color: inherit;
}

.resend-text {
  color: inherit;
}

/* Responsive design */
@media (max-width: 768px) {
  .banner-content {
    padding: 10px 12px;
    gap: 10px;
    min-height: 44px;
  }
  
  .banner-title {
    font-size: 14px;
  }
  
  .banner-subtitle {
    font-size: 12px;
  }
  
  .resend-button {
    padding: 7px 10px;
    font-size: 12px;
    min-height: 34px;
  }
}

@media (max-width: 600px) {
  .banner-content {
    flex-direction: column;
    align-items: flex-start;
    padding: 10px 10px;
    gap: 10px;
    min-height: auto;
  }
  
  .banner-text {
    width: 100%;
  }
  
  .title-row {
    gap: 5px;
  }
  
  .banner-actions {
    align-self: stretch;
    width: 100%;
  }
  
  .resend-button {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .banner-content {
    padding: 8px 8px;
    gap: 8px;
  }
  
  .title-row {
    gap: 4px;
  }
  
  .banner-title {
    font-size: 13px;
  }
  
  .banner-subtitle {
    font-size: 11px;
  }
  
  .resend-button {
    padding: 7px 10px;
    font-size: 11px;
    min-height: 34px;
  }
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style> 